import { useEffect, useState } from "react";
import { Col, Grid, message, Row, Typography } from "antd";
import useStore from "../../../hooks/useStore";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import getStoreDetails from "../../apiRepository/getStoreDetails";
import DeliverySettingsDisableDialog from "./DeliverySettingsDisableDialog";
import DeliverySettingsConfirmDialog from "./DeliverySettingsConfirmDialog";
import DeliverySettingsEnableDisableService from "./DeliverySettingsEnableDisableService";
import DeliverySettingsPriceSettings from "./DeliverySettingsPriceSettings";
import DeliverySettingsMenuSync from "./DeliverySettingsMenuSync";
import DeliverySettingsDisableDetails from "./DeliverySettingsDisableDetails";
import DeliverySettingsActionButton from "./DeliverySettingsActionButton";
import deliveryServiceTypes from "../../../constants/deliveryServiceTypes";

const { useBreakpoint } = Grid;
const { Title } = Typography;

export default function DeliverySettings({ deliveryService }) {
  const screens = useBreakpoint();
  const [messageApi, messageContextHolder] = message.useMessage({ top: 35 });

  // Hooks
  const { store, setStore } = useStore();
  const axiosPrivate = useAxiosPrivate();
  const {
    enabled,
    percentToCharge,
    roundPrice,
  } = store?.deliverySettings?.[deliveryService] || {};

  // Constant value
  const defaultSetting = {
    enabled,
    percentToCharge,
    roundPrice
  };

  // State
  const [settings, setSettings] = useState(defaultSetting);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isAPILoading, setIsAPILoading] = useState(false);
  const [isSettingMatch, setIsSettingMatch] = useState(false);
  const [menuSyncing, setMenuSyncing] = useState(false);

  useEffect(() => {
    const isMatch = Array(Object.values(settings)).toString() === Array(Object.values(defaultSetting)).toString();
    setIsSettingMatch(isMatch);
  }, [settings, defaultSetting]);

  const handleEnableDisable = () => {
    if (defaultSetting.enabled) {
      setShowDisableDialog(true);
      setSettings((prev) => ({ ...prev, enabled: false }));
    } else {
      setSettings((prev) => ({ ...prev, enabled: true }));
    }
  };

  const handleCloseEnableDisable = () => {
    setShowDisableDialog(false);
    handleReset();
  };

  const handleReset = () => {
    setSettings(defaultSetting);
  };

  const handleCloseDialog = () => {
    setShowDisableDialog(false);
    setShowConfirmDialog(false);
  };

  const handleGetStoreDetails = async () => {
    const storeDetails = await getStoreDetails({
      integratorId: store?.integratorId,
      storeId: store?.storeId,
      token: store?.token,
    }).catch((err) => {
      console.error(err);
    });
    setStore((prev) => ({ ...prev, ...storeDetails }));
  };

  const handleErrorMessage = (error) => {
    if (Array.isArray(error)) {
      (error || []).map(error => {
        messageApi.open({
          top: 100,
          type: "error",
          content: error,
        });
      })
    } else {
      messageApi.open({
        top: 100,
        type: "error",
        content: error,
      });
    }
  };

  const handleSuccessMessage = (message) => {
    messageApi.open({
      top: 100,
      type: "success",
      content: message,
    });
  };

  const handleSave = async (data) => {
    setIsAPILoading(true);

    const newSettings = { ...settings, ...data };
    if (newSettings.enabled === true) {
      delete newSettings.reason;
      delete newSettings.notes;
    }

    await axiosPrivate
      .patch(
        `/integrator-service/integrators/${store.integratorId}/stores/${store.storeId}`,
        {
          deliverySettings: {
            type: deliveryService,
            settings: newSettings,
          },
        },
      )
      .then(async (_res) => {
        handleCloseDialog();
        await handleGetStoreDetails();
        handleSuccessMessage("Successfully updated.");
      })
      .catch((err) => {
        console.error(err);
        let error = err?.response?.data?.errors || err;

        if (err.message === "Network Error") {
          error = "Failed to update store data.";
        }

        handleErrorMessage(error);
      })
      .finally(() => {
        setIsAPILoading(false);
        handleCloseDialog();
      });
  };

  const handleSync = async () => {
    setMenuSyncing(true);

    await axiosPrivate
      .post(
        `/integrator-service/integrators/${store.integratorId}/stores/${store.storeId}/menu-sync`,
        {
          service: deliveryServiceTypes[deliveryService].capitalize,
        },
      )
      .then(async ({ data }) => {
        await handleGetStoreDetails();
        handleSuccessMessage(data?.message || "Successfully synced.");
      })
      .catch((err) => {
        console.error(err);
        let error = err?.response?.data?.errors || err;

        if (err.message === "Network Error") {
          error = "Failed to sync store.";
        }

        handleErrorMessage(error);
      })
      .finally(() => {
        setMenuSyncing(false);
      });
  };

  return (
    <>
      <Row style={{ margin: screens.xl ? "3% 18%" : screens.lg ? "3% 10%" : "3% 3%" }}>
        <Col span={24} style={{ marginBottom: 15 }}>
          <Title level={3} style={{ margin: 0, color: "#515151" }}>
            DELIVERY SETTINGS
          </Title>
        </Col>
        <Col span={24}>
          <DeliverySettingsEnableDisableService
            deliveryService={deliveryService}
            settings={settings}
            handleEnableDisable={handleEnableDisable}
          />
        </Col>
        <Col span={24}>
          <DeliverySettingsPriceSettings
            deliveryService={deliveryService}
            defaultSetting={defaultSetting}
            settings={settings}
            setSettings={setSettings}
            isSettingMatch={isSettingMatch}
          />
        </Col>
        <Col span={24} style={{ marginBottom: 50 }}>
          {defaultSetting.enabled === false && isSettingMatch === true ?
            <DeliverySettingsDisableDetails
              deliveryService={deliveryService}
              store={store}
            />
            : null
          }
          {settings.enabled === true && isSettingMatch === false ?
            <DeliverySettingsActionButton
              handleReset={handleReset}
              setShowConfirmDialog={setShowConfirmDialog}
            />
            : null
          }
        </Col>
        <Col span={24}>
          <DeliverySettingsMenuSync
            deliveryService={deliveryService}
            defaultSetting={defaultSetting}
            store={store}
            menuSyncing={menuSyncing}
            handleSync={handleSync}
          />
        </Col>
      </Row >
      <DeliverySettingsDisableDialog
        showDisableDialog={showDisableDialog}
        setShowDisableDialog={setShowDisableDialog}
        handleSave={handleSave}
        handleClose={handleCloseEnableDisable}
        isSubmitting={isAPILoading}
      />
      <DeliverySettingsConfirmDialog
        showConfirmDialog={showConfirmDialog}
        setShowConfirmDialog={setShowConfirmDialog}
        handleSave={handleSave}
        isSubmitting={isAPILoading}
      />
      {messageContextHolder}
    </>
  );
};
